import {
  ClientEntity,
  OperationalEntity
} from "@common-core/coat-operational-hierarchy-appsync-model";

/**
 * Validates tenant data integrity across the hierarchy and extracts the tenant ID
 * from the ultimate parent's mapped operational entities.
 *
 * Resolution algorithm (see tenant-resolution-logic.md for full spec):
 * 1. Collect all tenant IDs from the hierarchy tree.
 * 2. >1 unique tenant IDs → Error (multi-tenant hierarchy).
 * 3. Exactly 1 tenant ID found → Must be on the ultimate parent's OEs, otherwise error.
 * 4. 0 tenant IDs found → If ultimate parent has OEs without tenancy, error. If no OEs, return undefined (new tenant needed).
 *
 * @param source - The root client entity of the hierarchy being created
 * @returns The tenant ID if one exists, or undefined if a new tenant needs to be created
 * @throws Error if tenancy cannot be established or multiple tenants detected
 */
export const validateAndExtractTenantIdForHierarchy = (
  source: ClientEntity
): string | undefined => {
  const ultimateParent = source.hierarchy || source;

  const extractTenantIds = (entities?: OperationalEntity[] | null): string[] =>
    (entities || [])
      .map(oe => oe.tenantId)
      .filter((id): id is string => id != null && id !== "");

  const collectAllTenantIds = (entity: ClientEntity): string[] => {
    const tenantIds: string[] = [];
    tenantIds.push(...extractTenantIds(entity.mappedOperationalEntities));
    if (entity.children) {
      entity.children.forEach(child => {
        tenantIds.push(...collectAllTenantIds(child));
      });
    }
    return tenantIds;
  };

  // Step 1: Collect all tenant IDs across the hierarchy
  const allTenantIds = collectAllTenantIds(ultimateParent);
  const uniqueTenantIds = new Set(allTenantIds);

  // Step 2: Multiple tenant IDs → error
  if (uniqueTenantIds.size > 1) {
    throw new Error(
      `This client hierarchy is already associated with multiple tenants: ${Array.from(uniqueTenantIds).join(", ")}. Data integrity error.`
    );
  }

  // Step 3: Exactly one tenant ID → validate it's on the ultimate parent
  if (uniqueTenantIds.size === 1) {
    const tenantId = Array.from(uniqueTenantIds)[0];
    const ultimateParentTenantIds = extractTenantIds(
      ultimateParent.mappedOperationalEntities
    );

    if (ultimateParentTenantIds.includes(tenantId)) {
      return tenantId;
    }

    throw new Error(
      `Data integrity error for ${source.caId}. Tenant ID "${tenantId}" exists only on descendant entities, not on the ultimate parent. Tenancy must be established at the ultimate parent level.`
    );
  }

  // Step 4: Zero tenant IDs → check if ultimate parent has OEs
  const mappedEntities = ultimateParent.mappedOperationalEntities;

  if (mappedEntities && mappedEntities.length > 0) {
    throw new Error(
      `Data integrity error for ${source.caId}. The ultimate parent has mapped operational entities, but none have tenancy established.`
    );
  }

  return undefined;
};
